import { Check, AlertTriangle } from 'lucide-react';
import { cn } from '../../lib/utils';

const DISPLAY_STEPS = [
  'lobby',
  'ingest',
  'triage',
  'planning',
  'create_hierarchy',
  'execute',
  'post_execute',
  'review',
  'publish',
  'learn',
  'complete',
] as const;

type DisplayStep = (typeof DISPLAY_STEPS)[number];

const STEP_LABELS: Record<DisplayStep, string> = {
  lobby: 'Lobby',
  ingest: 'Ingest',
  triage: 'Triage',
  planning: 'Plan',
  create_hierarchy: 'Hierarchy',
  execute: 'Execute',
  post_execute: 'Post-Exec',
  review: 'Review',
  publish: 'Publish',
  learn: 'Learn',
  complete: 'Complete',
};

type StepStatus = 'pending' | 'in_progress' | 'completed' | 'failed' | 'skipped' | 'inherited';

interface DisplayStepEntry {
  status: StepStatus;
  timestamp?: string;
  note?: string;
}

export function mapLegacyStage(stage: string, stalledAtStage?: string): Record<string, DisplayStepEntry> {
  const steps: Record<string, DisplayStepEntry> = {};
  const idx = DISPLAY_STEPS.indexOf(stage as DisplayStep);
  const stalledIdx = stalledAtStage ? DISPLAY_STEPS.indexOf(stalledAtStage as DisplayStep) : -1;
  const isFailed = stage === 'failed' || stage === 'escalated';
  const fallbackIdx = isFailed
    ? DISPLAY_STEPS.indexOf(stage === 'escalated' ? 'complete' : 'execute')
    : -1;
  const effectiveIdx = isFailed
    ? (stalledIdx >= 0 ? stalledIdx : fallbackIdx)
    : idx;

  for (let i = 0; i < DISPLAY_STEPS.length; i++) {
    const step = DISPLAY_STEPS[i];
    if (stage === 'complete' || i < effectiveIdx) {
      steps[step] = { status: 'completed' };
    } else if (i === effectiveIdx && isFailed) {
      steps[step] = { status: 'failed' };
    } else if (i === effectiveIdx) {
      steps[step] = { status: 'in_progress' };
    } else {
      steps[step] = { status: 'pending' };
    }
  }
  if (stage === 'complete') {
    steps.complete = { status: 'completed' };
  }
  return steps;
}

interface PipelineStageIndicatorProps {
  currentStage: string;
  stalledAtStage?: string;
  displaySteps?: Record<string, DisplayStepEntry>;
  onStageClick?: (step: string) => void;
  className?: string;
}

export function PipelineStageIndicator({
  currentStage,
  stalledAtStage,
  displaySteps,
  onStageClick,
  className,
}: PipelineStageIndicatorProps) {
  const steps = displaySteps || mapLegacyStage(currentStage, stalledAtStage);

  return (
    <div className={cn('w-full', className)}>
      <div className="flex flex-wrap items-center gap-x-0.5 gap-y-1.5">
        {DISPLAY_STEPS.map((step, index) => {
          const entry = steps[step] || { status: 'pending' };
          const isDone = entry.status === 'completed' || entry.status === 'inherited';
          const isCurrent = entry.status === 'in_progress';
          const isFailed = entry.status === 'failed';
          const isSkipped = entry.status === 'skipped';
          const isPending = entry.status === 'pending';
          const clickable = Boolean(onStageClick);

          return (
            <div key={step} className="flex items-center">
              <button
                type="button"
                disabled={!clickable}
                onClick={() => onStageClick?.(step)}
                className={cn(
                  'flex flex-col items-center gap-0.5',
                  clickable && 'cursor-pointer hover:opacity-80',
                  !clickable && 'cursor-default',
                )}
                title={entry.note || STEP_LABELS[step]}
              >
                <div
                  className={cn(
                    'flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-2 text-[10px] font-bold transition-colors',
                    isDone && 'border-emerald-500 bg-emerald-500 text-white dark:border-emerald-400 dark:bg-emerald-600',
                    isCurrent && 'border-blue-600 bg-blue-600 text-white dark:border-blue-500 dark:bg-blue-500 animate-pulse',
                    isFailed && 'border-red-500 bg-red-100 text-red-700 dark:border-red-400 dark:bg-red-900/40 dark:text-red-200',
                    isSkipped && 'border-gray-300 bg-gray-100 text-gray-400 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-500',
                    isPending && 'border-gray-200 bg-white text-gray-400 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-500',
                  )}
                >
                  {isDone ? (
                    <Check className="h-3 w-3" strokeWidth={3} />
                  ) : isFailed ? (
                    <AlertTriangle className="h-3 w-3" strokeWidth={2.5} />
                  ) : (
                    index + 1
                  )}
                </div>
                <span
                  className={cn(
                    'max-w-[52px] text-center text-[9px] font-medium leading-tight',
                    isDone && 'text-emerald-700 dark:text-emerald-300',
                    isCurrent && 'text-blue-700 dark:text-blue-300',
                    isFailed && 'text-red-600 dark:text-red-300',
                    (isPending || isSkipped) && 'text-gray-400 dark:text-gray-500',
                  )}
                >
                  {STEP_LABELS[step]}
                </span>
              </button>
              {index < DISPLAY_STEPS.length - 1 && (
                <div
                  className={cn(
                    'mx-0.5 mb-4 h-0.5 w-2 shrink-0 rounded',
                    isDone ? 'bg-emerald-400 dark:bg-emerald-600' : 'bg-gray-200 dark:bg-gray-600',
                  )}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
